/**
 * Complaints ranked by how well they are evidenced, against who addresses
 * them. A gap — a pain the skeptic stands behind that no product addresses —
 * is the opportunity, and it sorts to the top. A gap where too few product
 * pages were read to know is marked unconfirmed (see `gapCheck`).
 */
import { useMemo, useState } from 'react';
import { CARD } from './tableLayout';
import type { BoardroomPainGap, BoardroomPainRow } from '../../../types';
import { VerdictBadge } from '../ItemCard';
import Critiques from './Critiques';
import MissingList, { type OnDig } from './MissingList';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { useReportSeen } from '../useSeen';
import NewHere from './NewHere';
import { gapExplain, gapTone, gapTotals, gapWord, isConfirmedGap, readWords } from './gapCheck';
import {
  cutoff,
  dotsByYear,
  heardLabel,
  isOlder,
  SINCE_WORDS,
  splitBySince,
  timelineYears,
  type Since,
} from './painTimeline';

/** ONE DESCRIPTION PER COLUMN. `value` is what the file gets and what sort
 *  compares; `cell` is what the reader sees. */
export function painColumns(onOpen: (id: number) => void): Column<BoardroomPainRow>[] {
  return [
    {
      key: 'pain',
      label: 'Pain',
      width: 30,
      always: true,
      value: r => r.title,
      cell: r => (
        <>
          <button type="button" onClick={() => onOpen(r.pain_id)} className="text-gray-800 dark:text-gray-200 hover:underline text-left">
            {r.title}
          </button>
          {r.gap && (
            <span
              className={`ml-1.5 text-[11px] px-1 py-0.5 rounded ${gapTone(r)}`}
              title={gapExplain(r)}
            >
              {isConfirmedGap(r) ? 'gap' : 'gap?'}
            </span>
          )}
          {r.ideas.length > 0 && (
            <div className="text-[11px] text-indigo-600 dark:text-indigo-400">idea: {r.ideas.map(i => i.title).join(', ')}</div>
          )}
        </>
      ),
    },
    {
      key: 'validation',
      label: 'Validation',
      width: 14,
      // "Not checked" is a state, not a blank: it sorts with the rest.
      value: r => (r.unchecked ? 'not validated' : r.verdict ?? 'not validated'),
      cell: r => (
        <>
          {r.verdict ? <VerdictBadge verdict={r.verdict} /> : <span className="text-gray-400">not validated</span>}
          <Critiques rows={r.critique_rows} onOpen={onOpen} className="ml-1" />
        </>
      ),
    },
    {
      key: 'evidence',
      label: 'Evidence',
      width: 9,
      align: 'right',
      value: r => r.evidence,
      cell: r => <span className="font-mono text-xs text-gray-500">{r.evidence}</span>,
    },
    {
      // WHERE IT WAS SAID. A count of evidence rows never said whether they
      // were different people; the hosts do, and they are clickable.
      key: 'sources',
      label: 'Said on',
      width: 20,
      sortable: false,
      // `sources` and `independent_sources` are both required on the row and the
      // server always sends both (`BoardroomPainRow`; `schemas.py` defaults the
      // count to 0). They were read two different ways here — one defaulted,
      // one bare — which reads as "this one can be missing", and if it ever
      // were, the defaulted field would save the cell while the bare one
      // rendered "undefined voices" in the amber one-voice state. Same rule for
      // both, and it matches `ideas` and `critique_rows` above.
      value: r => r.sources.map(s => s.url).join(' / '),
      cell: r =>
        r.sources.length === 0 ? (
          <span className="text-gray-400 italic">no page recorded</span>
        ) : (
          <div className="flex flex-wrap items-baseline gap-1">
            <span
              className={`text-[11px] ${
                r.independent_sources > 1 ? 'text-gray-500' : 'text-amber-700 dark:text-amber-300'
              }`}
              title={
                r.independent_sources > 1
                  ? `${r.independent_sources} separate voices`
                  : 'one voice - the same person or thread'
              }
            >
              {r.independent_sources} voice{r.independent_sources === 1 ? '' : 's'}
            </span>
            {/* Customers first; a seller's own site is shown last, struck through. */}
            {[...r.sources]
              .sort((a, b) => Number(!!a.seller) - Number(!!b.seller))
              .slice(0, 3)
              .map(s => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                title={`${s.quote ? `“${s.quote}”` : s.url}${
                  s.said_at ? ` - said ${heardLabel(s.said_at)}${s.said_from === 'page date' ? ' (the page\'s date)' : ''}` : ''
                }${s.seller ? ' - a seller\'s own site, not counted as a customer' : ''}`}
                className={`text-xs hover:underline ${
                  s.seller ? 'text-gray-400 line-through dark:text-gray-500' : 'text-indigo-600 dark:text-indigo-400'
                }`}
              >
                {s.url.replace(/^https?:\/\//, '').split('/')[0]}
                {s.quote_found_in_source === false && (
                  <span className="text-amber-600" title="the quote is not in this page"> ⚠</span>
                )}
              </a>
            ))}
            {r.sources.length > 3 && (
              <span className="text-[11px] text-gray-400">+{r.sources.length - 3}</span>
            )}
          </div>
        ),
    },
    {
      // WHEN IT WAS LAST SAID. A complaint nobody has made in years may be
      // fixed: older than two years reads grey.
      key: 'heard',
      label: 'Last heard',
      width: 9,
      value: r => r.last_heard ?? '',
      cell: r =>
        r.last_heard ? (
          <span
            className={isOlder(r.last_heard, cutoff('2y')) ? 'text-gray-400' : 'text-gray-700 dark:text-gray-200'}
            title={`${r.recent_voices ?? 0} voice${r.recent_voices === 1 ? '' : 's'} in the last two years${
              r.undated ? ` · ${r.undated} source${r.undated === 1 ? '' : 's'} with no date` : ''
            }`}
          >
            {heardLabel(r.last_heard)}
          </span>
        ) : (
          <span className="text-gray-400 italic">date unknown</span>
        ),
    },
    {
      // THE FINDING. Sorting by this puts the gaps together, which is the
      // question the table exists to answer.
      key: 'gap',
      label: 'Gap',
      width: 8,
      value: r => gapWord(r),
      cell: r =>
        r.gap ? (
          <span className={isConfirmedGap(r) ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'} title={gapExplain(r)}>
            {gapWord(r)}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      key: 'addressed',
      label: 'Addressed by',
      width: 39,
      sortable: false,
      value: r => r.addressed_by.map(a => (a.via_feature ? `${a.title} (${a.via_feature})` : a.title)).join(' / '),
      cell: r =>
        r.addressed_by.length === 0 ? (
          <span className={!r.gap ? 'text-gray-400 italic' : isConfirmedGap(r) ? 'text-rose-700 dark:text-rose-300' : 'text-amber-700 dark:text-amber-300'}>
            {!r.gap ? 'not checked' : isConfirmedGap(r) ? 'nobody' : 'none found yet'}
            {r.gap && r.field_read && <span className="text-gray-400"> · {readWords(r.field_read)}</span>}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {r.addressed_by.map(a => (
              <button
                key={a.product_id}
                type="button"
                onClick={() => onOpen(a.product_id)}
                title={a.via_feature ? `through ${a.via_feature}` : 'directly'}
                className="px-1.5 py-0.5 rounded-full border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 text-xs hover:bg-sky-50 dark:hover:bg-sky-900/20"
              >
                {a.title}
                {a.via_feature && <span className="text-gray-400"> · {a.via_feature}</span>}
              </button>
            ))}
          </div>
        ),
    },
  ];
}

const GROUP_KEY = 'jcseo.boardroom.paingap.group';
const SINCE_KEY = 'jcseo.boardroom.paingap.since';
const VIEW_KEY = 'jcseo.boardroom.paingap.view';

function stored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key) as T | null;
    return raw && allowed.includes(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode: it holds for this visit */
  }
}

/**
 * THE COMPLAINTS ALONG A LINE OF YEARS: a dot for each place it was said, in
 * its year, so an old complaint shows where it sits and the recent ones stand
 * out. Dots before the cut-off are grey; a seller's own site is hollow.
 */
export function PainTimeline({
  rows,
  cut,
  onOpen,
}: {
  rows: BoardroomPainRow[];
  cut: string | null;
  onOpen: (id: number) => void;
}) {
  const years = timelineYears(rows);
  if (!years.length) {
    return <p className="p-3 text-sm text-gray-500 dark:text-gray-400">No complaint has a date yet.</p>;
  }
  const undatedTotal = rows.reduce((n, r) => n + dotsByYear(r).undated, 0);
  return (
    <div className="overflow-x-auto" data-testid="pain-timeline">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] text-gray-400">
            <th className="px-2 py-1 text-left font-normal">Complaint</th>
            {years.map(y => (
              <th key={y} className="px-1 py-1 text-center font-normal tabular-nums">
                {y}
              </th>
            ))}
            <th className="px-2 py-1 text-left font-normal">Last heard</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const { byYear } = dotsByYear(r);
            const old = isOlder(r.last_heard, cut);
            return (
              <tr key={r.pain_id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => onOpen(r.pain_id)}
                    className={`text-left hover:underline ${old ? 'text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}
                  >
                    {r.title}
                  </button>
                </td>
                {years.map(y => (
                  <td key={y} className="px-1 py-1 text-center">
                    {(byYear.get(y) ?? []).map(d => (
                      <a
                        key={d.url}
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        title={`${heardLabel(d.when)}${d.from === 'page date' ? ' (the page\'s date)' : ''}${
                          d.quote ? `: “${d.quote}”` : ''
                        }${d.seller ? ' - a seller\'s own site' : ''}`}
                        className={`inline-block px-px text-xs ${
                          isOlder(d.when, cut) ? 'text-gray-300 dark:text-gray-600' : 'text-indigo-600 dark:text-indigo-300'
                        }`}
                      >
                        {d.seller ? '○' : '●'}
                      </a>
                    ))}
                  </td>
                ))}
                <td className={`px-2 py-1 text-xs ${old ? 'text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                  {heardLabel(r.last_heard) || <span className="italic text-gray-400">date unknown</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {undatedTotal > 0 && (
        <p className="px-2 py-1 text-[11px] text-gray-400">
          {undatedTotal} source{undatedTotal === 1 ? '' : 's'} with no date, not placed on the line.
        </p>
      )}
    </div>
  );
}
// Either a complaint about the whole field, or one no seat has placed yet.
const NO_ONE = 'No company named';

function storedGrouped(): boolean {
  try {
    return window.localStorage.getItem(GROUP_KEY) !== 'none';
  } catch {
    return true;
  }
}

/**
 * The pains with a company's together: groups with the most gaps first, then
 * the biggest, and the complaints about no one player last. Pure.
 *
 * ONE COMPANY'S COMPLAINTS READ AS ONE STORY. Ranked as one list, a hundred
 * pains about forty products read as a pile; grouped, "SimplePractice: eleven
 * complaints, four nobody addresses" is the finding. Who a pain is about is
 * what the seat that wrote it recorded (`about`), never read off its title.
 */
export function groupPains(rows: BoardroomPainRow[]): { rows: BoardroomPainRow[]; label: (r: BoardroomPainRow) => string } {
  const by = new Map<string, BoardroomPainRow[]>();
  for (const r of rows) {
    const k = r.about ? `co:${r.about.id}` : 'none';
    by.set(k, [...(by.get(k) ?? []), r]);
  }
  const groups = Array.from(by.entries()).sort(([ka, a], [kb, b]) => {
    if ((ka === 'none') !== (kb === 'none')) return ka === 'none' ? 1 : -1;
    const gaps = (rs: BoardroomPainRow[]) => rs.filter(isConfirmedGap).length;
    return gaps(b) - gaps(a) || b.length - a.length || (a[0].about?.title ?? '').localeCompare(b[0].about?.title ?? '');
  });
  const labels = new Map<number, string>();
  for (const [, rs] of groups) {
    const gaps = rs.filter(isConfirmedGap).length;
    const name = rs[0].about?.title ?? NO_ONE;
    const label = `${name} · ${rs.length} pain${rs.length === 1 ? '' : 's'}${gaps ? `, ${gaps} nobody addresses` : ''}`;
    for (const r of rs) labels.set(r.pain_id, label);
  }
  return { rows: groups.flatMap(([, rs]) => rs), label: r => labels.get(r.pain_id) ?? NO_ONE };
}

export default function PainGapReport({
  data,
  onOpen,
  onDig,
  digging,
  unseen,
  onSeen,
  roomName,
}: {
  data: BoardroomPainGap;
  onOpen: (itemId: number) => void;
  onDig: OnDig;
  digging: boolean;
  /** Ids that changed and have not been looked at. */
  unseen?: ReadonlySet<number>;
  /** Ids now on screen — the caller drops them from the badge. */
  onSeen?: (ids: number[]) => void;
  roomName?: string;
}) {
  const columns = useMemo(() => painColumns(onOpen), [onOpen]);
  const empty = data.totals.pains === 0;
  // BY COMPANY UNLESS THEY SAY OTHERWISE, remembered in this browser.
  const [grouped, setGrouped] = useState(storedGrouped);
  const setGroupedAndRemember = (next: boolean) => {
    setGrouped(next);
    try {
      window.localStorage.setItem(GROUP_KEY, next ? 'company' : 'none');
    } catch {
      /* private mode: it holds for this visit */
    }
  };
  // RECENT FEEDBACK FIRST: complaints last heard before the cut-off fold
  // away, counted, rather than vanish. Unknown dates stay - unknown is not old.
  const [since, setSince] = useState<Since>(() => stored(SINCE_KEY, ['all', '2y', '1y'] as const, '2y'));
  const [view, setView] = useState<'list' | 'timeline'>(() =>
    stored(VIEW_KEY, ['list', 'timeline'] as const, 'list'),
  );
  const [showOlder, setShowOlder] = useState(false);
  const cut = cutoff(since);
  const split = useMemo(() => splitBySince(data.rows, cut), [data.rows, cut]);
  const visible = showOlder ? data.rows : split.shown;
  const groupedRows = useMemo(() => groupPains(visible), [visible]);
  // ONE TRACKER, the table and the questions under it: two of them and the
  // questions list pruned the table's marks as ids it has no row for.
  const seen = useReportSeen(unseen, onSeen, [
    ...data.rows.map(r => r.pain_id),
    ...data.missing.map(m => m.item_id),
  ]);
  return (
    <div className="flex flex-col gap-3 min-h-0">
      <NewHere count={seen.waitingHere} glowing={seen.glowing} onJump={seen.next} onClearAll={seen.readAll} />
      {!empty && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {gapTotals(data.totals)} · {data.totals.addressed} of {data.totals.pains} pains addressed by someone
          <div className="ml-auto flex items-center gap-1" data-testid="pain-view">
            {(['list', 'timeline'] as const).map(v => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  setView(v);
                  remember(VIEW_KEY, v);
                }}
                className={`rounded px-1.5 py-0.5 ${
                  view === v ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500'
                }`}
              >
                {v === 'list' ? 'List' : 'Timeline'}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            Since
            <select
              value={since}
              onChange={e => {
                const next = e.target.value as Since;
                setSince(next);
                setShowOlder(false);
                remember(SINCE_KEY, next);
              }}
              className="rounded border border-gray-300 bg-transparent px-1 py-0.5 dark:border-gray-600"
              data-testid="pain-since"
            >
              {(Object.keys(SINCE_WORDS) as Since[]).map(k => (
                <option key={k} value={k}>
                  {SINCE_WORDS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-gray-500 dark:text-gray-400" data-testid="pain-group">
            <input type="checkbox" checked={grouped} onChange={e => setGroupedAndRemember(e.target.checked)} />
            Group by company
          </label>
        </div>
      )}
      {!empty && split.older.length > 0 && (
        <button
          type="button"
          onClick={() => setShowOlder(o => !o)}
          className="self-start text-xs text-gray-500 hover:underline dark:text-gray-400"
          data-testid="pain-older"
        >
          {showOlder
            ? `Hide the ${split.older.length} last heard before ${SINCE_WORDS[since].toLowerCase()} ago`
            : `${split.older.length} hidden as older · show`}
        </button>
      )}
      {!empty && view === 'timeline' && (
        <div className={CARD}>
          <PainTimeline rows={visible} cut={cut} onOpen={onOpen} />
        </div>
      )}
      {!empty && view === 'list' && (
        <div className={CARD}>
          <DataTable
            table="pain_gap"
            room={roomName}
            seen={seen}
            columns={columns}
            rows={grouped ? groupedRows.rows : visible}
            section={grouped ? groupedRows.label : undefined}
            rowKey={r => `pain-${r.pain_id}`}
            rowClass={r => (!r.gap ? '' : isConfirmedGap(r) ? 'bg-rose-50/60 dark:bg-rose-900/10' : 'bg-amber-50/40 dark:bg-amber-900/10')}
            rowData={r => ({ 'data-gap': !r.gap ? 'false' : isConfirmedGap(r) ? 'true' : 'unconfirmed' })}
          />
        </div>
      )}
      <MissingList missing={data.missing} empty={empty} onOpen={onOpen} onDig={onDig} digging={digging} seen={seen} />
    </div>
  );
}
